/**
 * Client-side image compression before upload.
 * Reduces 12 MP phone photos (~5 MB) to ~400 KB while preserving OCR legibility.
 */

const MAX_DIM = 1800;
const QUALITY = 0.85;

/**
 * Check if browser can handle the file type for canvas compression.
 * HEIC/HEIF are NOT supported by browsers — send as-is to server (which converts via pillow-heif).
 */
function canCompressClientSide(file) {
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  if (name.endsWith(".heic") || name.endsWith(".heif")) return false;
  if (type === "image/heic" || type === "image/heif") return false;
  return type.startsWith("image/");
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Compress a single image file. Returns the original if compression fails or file is already small.
 */
export async function compressImage(file) {
  if (!canCompressClientSide(file)) return file;
  // Skip files already small (<600 KB)
  if (file.size < 600 * 1024) return file;

  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const img = await loadImage(dataUrl);
    const { width, height } = img;
    const scale = Math.min(1, MAX_DIM / Math.max(width, height));
    if (scale === 1 && file.size < 1800 * 1024) return file; // no resize + not huge → keep

    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
    if (!blob) return file;
    // If compressed is actually bigger (rare), keep original
    if (blob.size >= file.size) return file;

    const newName = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}

export async function compressMany(files, onProgress) {
  const out = [];
  for (let i = 0; i < files.length; i++) {
    const before = files[i].size;
    const compressed = await compressImage(files[i]);
    out.push(compressed);
    if (onProgress) onProgress({ index: i + 1, total: files.length, before, after: compressed.size });
  }
  return out;
}
